<template>
  <div v-if="collection">
    <align class="header" flex>
      <align small-gap>
        <h3 class="secondary-font">
          {{ collectionName }}
        </h3>

        <dropdown
          v-if="collectionOptions.length"
          :align="bottom ? 'top-left' : 'center'"
          :active="menuOpen"
          @close="menuOpen = false"
        >
          <icon-button icon="expand_more" @click="menuOpen = !menuOpen" />

          <template #menu>
            <dropdown-menu
              :options="collectionOptions"
              @select="switchCollection"
            />
          </template>
        </dropdown>
      </align>

      <icon-link as="router-link" :to="`/collection?id=${collectionId}`">
        View Collection
      </icon-link>
    </align>

    <div class="grid">
      <stat-item
        size="xlarge"
        :success="collectionStats.dailyDollarChange > 0"
        :error="collectionStats.dailyDollarChange < 0"
        label="Daily $ Change"
        :value="`${collectionStats.dailyDollarChange > 0 ? '+' : ''}${$filters.currency(collectionStats.dailyDollarChange)}`"
      />

      <stat-item
        size="xlarge"
        :success="collectionStats.dailyPercentChange > 0"
        :error="collectionStats.dailyPercentChange < 0"
        label="Daily % Change"
        :value="`${collectionStats.dailyPercentChange > 0 ? '+' : ''}${$filters.percentage(collectionStats.dailyPercentChange)}`"
      />

      <stat-item
        size="xlarge"
        success
        label="Total Value"
        :value="$filters.currency(collection.totalValue)"
      />

      <stat-item
        size="xlarge"
        :success="collectionStats.profit > 0"
        :error="collectionStats.profit < 0"
        label="Potential Profit"
        :value="`${collectionStats.profit > 0 ? '+' : ''}${$filters.currency(collectionStats.profit)}`"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import { getCollectionStats, combineCollectionData } from '@cardladder/shared/utils/collection.util';
import StatItem from '@cardladder/shared/components/StatItem';

import { authService } from '@/services';

export default {
  components: {
    StatItem,
  },

  props: {
    bottom: Boolean,
  },

  data() {
    return {
      menuOpen: false,
    };
  },

  watch: {
    collection: {
      handler(value) {
        if (!value) {
          this.updateDashboardCollectionId(this.collections[0].id);
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collections: (state) => state.collections,
    }),

    collectionId() {
      return this.user?.dashboardWidgets.find(({ component }) => component === 'CollectionWidget')?.collectionId;
    },

    collection() {
      if (this.collectionId === 'total') {
        return combineCollectionData(this.collections);
      }

      return this.collections.find(({ id }) => id === this.collectionId);
    },

    collectionStats() {
      return getCollectionStats(this.collection);
    },

    collectionName() {
      if (!this.collection || !this.collection.name) {
        return 'Collection';
      }

      return this.collection.name;
    },

    collectionOptions() {
      if (!this.collections || this.collections.length < 1) {
        return [];
      }

      const collectionOptions = this.collections
        .filter(({ id }) => id !== this.collectionId)
        .map(({ id, name }) => ({
          value: id,
          label: name || 'Collection',
        }))
        .sort((a, b) => a.label?.localeCompare(b.label));

      if (collectionOptions.length > 0) {
        collectionOptions.unshift({
          value: 'total',
          label: 'Total',
        });
      }

      return collectionOptions;
    },
  },

  methods: {
    switchCollection(collectionId) {
      this.menuOpen = false;
      this.updateDashboardCollectionId(collectionId);
    },

    async updateDashboardCollectionId(collectionId) {
      try {
        await authService.updateUser(this.user.uid, {
          dashboardWidgets: [
            ...this.user.dashboardWidgets.map((item) => {
              if (item.component === 'CollectionWidget') {
                return {
                  ...item,
                  collectionId,
                };
              }

              return item;
            }),
          ],
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(4, 1fr);

  @media($large-down) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.header {
  margin: 0 0 $gp;
}

h3 {
  margin: 0;
}
</style>
